// @flow

import type { JSONValue, JSONObject } from 'common/types';
import type {
  JSONAPIPayload,
  JSONAPIRelationships,
  JSONAPIResource,
  JSONAPILinkage
} from 'common/jsonApi/types';

import {
  nullable,
  object,
  record,
  jsonValue,
  jsonObject,
  id,
  string,
  array,
  items,
  map
} from 'common/json/extract';

type NullOneOrMany<V> = null | V | Array<V>;

function nullOneOrMany<V>(
  extract: JSONObject => V
): JSONValue => NullOneOrMany<V> {
  return json => {
    if (json === null) {
      return null;
    } else if (Array.isArray(json)) {
      return array(items(object(extract)))(json);
    } else if (typeof json === 'object') {
      return extract(json);
    }
    return null;
  };
}

const jsonApiLinkage: JSONValue => NullOneOrMany<JSONAPILinkage> = nullOneOrMany(
  record({
    id,
    type: string,
    meta: jsonValue
  })
);

const jsonApiRelationships: JSONValue => JSONAPIRelationships = object(
  map(
    string,
    object(
      record({
        data: jsonApiLinkage,
        links: jsonValue,
        meta: jsonValue
      })
    )
  )
);

const jsonApiResource: JSONValue => NullOneOrMany<JSONAPIResource> = nullOneOrMany(
  // $FlowFixMe extractors do not handle possibly undefined keys
  record({
    id,
    type: string,
    // The problem is here...
    attributes: nullable(object(jsonObject)),
    // ...and here
    relationships: nullable(jsonApiRelationships),
    links: jsonValue,
    meta: jsonValue
  })
);

// $FlowFixMe extractors do not handle possibly undefined keys
const singleJsonApiResource: JSONValue => JSONAPIResource = record({
  id,
  type: string,
  // The problem is here...
  attributes: nullable(object(jsonObject)),
  // ...and here
  relationships: nullable(jsonApiRelationships),
  links: jsonValue,
  meta: jsonValue
});

const payloadFromJson: JSONValue => JSONAPIPayload = object(
  record({
    data: jsonApiResource,
    included: nullable(array(items(singleJsonApiResource))),
    meta: jsonValue,
    links: jsonValue,
    jsonapi: jsonValue
  })
);

export default function jsonApiPayload<Content>(
  extract: JSONAPIPayload => Content
): JSONValue => Content {
  return json => extract(payloadFromJson(json));
}
